import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Search,
  Plus,
  Trash2,
  ChevronDown,
  Check,
  X,
  Paperclip,
  ArrowLeft,
} from "lucide-react";
import api from "../../../api/axios";
import {
  getQualityMeta,
  createQualityMeta,
  updateQualityMeta,
} from "../../../api/quality/meta";
import MobileAttachmentBox from "../../../components/common/papers/MobileAttachmentBox";

export default function QualityInspectionNew() {
  const { id: routeProjectId, dns: doc_dns } = useParams();
  const [dns, setDns] = useState(doc_dns || "");
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const attachmentRef = useRef(null);

  // ------------------------------
  // 상태 관리
  // ------------------------------
  const [form, setForm] = useState({
    doc_date: new Date().toISOString().slice(0, 10),
    status: "draft",
    result: "",
    title: "",
    note: "",
  });

  const [officialMeta, setOfficialMeta] = useState({
    supplier_name: "",
    delivery_note_no: "",
    lot_no: "",
    place: "",
    material_name: "",
    spec: "",
    quantity: "",
    unit: "",
    inspector: "",
    reviewer: "",
    approver: "",
  });

  const [inspectionItems, setInspectionItems] = useState([
    { item: "", criteria: "", result: "", remark: "" },
  ]);

  // 섹션 접힘 상태
  const [expandedSections, setExpandedSections] = useState({
    docInfo: true,
    deliveryInfo: true,
    materialInfo: true,
    inspection: true,
    note: false,
    inspector: false,
    attachment: true,
  });

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const projectId =
    routeProjectId ||
    localStorage.getItem("projectId") ||
    localStorage.getItem("projectCode") ||
    "";

  // ------------------------------
  // 데이터 로드
  // ------------------------------
  useEffect(() => {
    if (!doc_dns) return;
    async function loadDetail() {
      try {
        const res = await getQualityMeta({ dns: doc_dns });
        const meta = res?.meta || {};

        if (meta.form) {
          setForm({
            doc_date: meta.form.doc_date || "",
            status: meta.form.status || "draft",
            result: meta.form.result || "",
            title: meta.form.title || "",
            note: meta.form.note || "",
          });
        }

        if (meta.officialMeta) {
          setOfficialMeta(meta.officialMeta);
        }

        if (meta.items) {
          setInspectionItems(meta.items);
        }
      } catch (err) {
        console.error(err);
        alert("검측요청서 문서를 불러오지 못했습니다.");
      }
    }

    loadDetail();
  }, [doc_dns]);

  // ------------------------------
  // 자재 검색 로직
  // ------------------------------
  const [itemSearch, setItemSearch] = useState("");
  const [itemResults, setItemResults] = useState([]);
  const [itemLoading, setItemLoading] = useState(false);
  const [showItemList, setShowItemList] = useState(false);

  const fetchItems = useCallback(async () => {
    if (!itemSearch.trim()) return;
    setItemLoading(true);
    setShowItemList(false);

    try {
      const res = await api.get("material/items/", {
        params: { project: projectId, q: itemSearch },
      });

      const list = Array.isArray(res.data?.results)
        ? res.data.results
        : Array.isArray(res.data)
        ? res.data
        : [];

      setItemResults(list);
      setShowItemList(true);
    } catch (e) {
      console.error("item search error:", e);
      setItemResults([]);
    } finally {
      setItemLoading(false);
    }
  }, [projectId, itemSearch]);

  const selectItem = (it) => {
    onChangeMeta("material_name", it.name || "");
    onChangeMeta("spec", it.spec || "");
    onChangeMeta("unit", it.unit || "EA");
    setShowItemList(false);
    setItemSearch("");
  };

  // ------------------------------
  // 핸들러
  // ------------------------------
  const onChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const onChangeMeta = (k, v) => setOfficialMeta((p) => ({ ...p, [k]: v }));

  const addRow = () =>
    setInspectionItems((prev) => [
      ...prev,
      { item: "", criteria: "", result: "", remark: "" },
    ]);

  const removeRow = (idx) =>
    setInspectionItems((prev) => prev.filter((_, i) => i !== idx));

  const updateRow = (idx, k, v) =>
    setInspectionItems((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [k]: v } : r))
    );

  // 자동 제목 생성
  const autoTitle = useMemo(() => {
    const m = officialMeta.material_name?.trim();
    const s = officialMeta.spec?.trim();
    const dn = officialMeta.delivery_note_no?.trim();
    if (m && s && dn) return `[검수] ${m} (${s}) - ${dn}`;
    if (m && s) return `[검수] ${m} (${s})`;
    if (m) return `[검수] ${m}`;
    return "";
  }, [officialMeta]);

  // 합격/불합격 집계
  const counts = useMemo(() => {
    const total = inspectionItems.length;
    const pass = inspectionItems.filter((r) => r.result === "pass").length;
    const fail = inspectionItems.filter((r) => r.result === "fail").length;
    return { total, pass, fail };
  }, [inspectionItems]);

  // 저장 로직
  const handleSave = async () => {
    if (saving) return;
    setSaving(true);

    try {
      const body = {
        layout: null,
        values: {
          project: projectId,
          module: "quality",
          doc_type: "MI",
          dns,
          meta: {
            form: {
              ...form,
              title: form.title?.trim() || autoTitle,
            },
            officialMeta,
            items: inspectionItems,
            summary: counts,
            __client__: { form_type: "MI" },
          },
        },
      };

      let result;
      if (dns) {
        result = await updateQualityMeta(dns, body.values);
      } else {
        result = await createQualityMeta(body.values);
      }

      const newDns = result?.dns || result?.doc_dns || dns;

      // 첨부파일 저장 (Ref 호출)
      if (newDns && attachmentRef.current) {
        await attachmentRef.current.save(newDns);
      }

      if (newDns) {
        setDns(newDns);
        alert("저장되었습니다.");
        // 저장 후 뷰 페이지로 이동하거나 유지
        navigate(
          `/project/${projectId}/quality/incoming/${encodeURIComponent(
            newDns
          )}/view`
        );
      }
    } catch (err) {
      console.error("저장 실패:", err);
      alert("저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => navigate(-1);

  // ------------------------------
  // 렌더링 (모바일 레이아웃)
  // ------------------------------
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* 모바일 헤더 */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100 px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={handleBack} className="p-1 -ml-1 text-gray-600">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-bold text-gray-900">자재검수기록</h1>
        </div>
        <div className="text-xs font-medium px-2 py-1 bg-blue-50 text-blue-600 rounded">
          {form.status === "draft" ? "작성중" : "완료"}
        </div>
      </header>

      {/* 본문 영역 */}
      <main className="flex-1 px-4 pt-4 pb-24 overflow-y-auto">
        {/* 요약 카드 */}
        <div className="mb-4 p-4 rounded-2xl bg-white shadow-sm border border-blue-100/50">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-bold text-gray-800">
              검사 현황 요약
            </span>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
              {form.doc_date}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <SummaryBox
              label="총 항목"
              value={counts.total}
              color="text-gray-800"
            />
            <SummaryBox
              label="합격"
              value={counts.pass}
              color="text-green-600"
            />
            <SummaryBox
              label="불합격"
              value={counts.fail}
              color="text-red-500"
            />
          </div>
        </div>

        <div className="space-y-3">
          {/* 1. 문서 정보 */}
          <Section
            title="문서 정보"
            expanded={expandedSections.docInfo}
            onToggle={() => toggleSection("docInfo")}
          >
            <InputField
              label="제목"
              value={form.title}
              onChange={(v) => onChange("title", v)}
              placeholder={autoTitle || "자동 생성됨"}
            />
            <InputField
              label="검사일"
              type="date"
              value={form.doc_date}
              onChange={(v) => onChange("doc_date", v)}
            />
          </Section>

          {/* 2. 입·납품 정보 */}
          <Section
            title="입·납품 정보"
            expanded={expandedSections.deliveryInfo}
            onToggle={() => toggleSection("deliveryInfo")}
          >
            <InputField
              label="공급업체"
              value={officialMeta.supplier_name}
              onChange={(v) => onChangeMeta("supplier_name", v)}
              placeholder="공급업체명"
            />
            <div className="grid grid-cols-2 gap-2">
              <InputField
                label="납품서 번호"
                value={officialMeta.delivery_note_no}
                onChange={(v) => onChangeMeta("delivery_note_no", v)}
                placeholder="DN-001"
              />
              <InputField
                label="LOT/Batch"
                value={officialMeta.lot_no}
                onChange={(v) => onChangeMeta("lot_no", v)}
                placeholder="LOT-001"
              />
            </div>
            <InputField
              label="검사 장소"
              value={officialMeta.place}
              onChange={(v) => onChangeMeta("place", v)}
              placeholder="현장 하치장 등"
            />
          </Section>

          {/* 3. 자재 정보 */}
          <Section
            title="자재 정보"
            expanded={expandedSections.materialInfo}
            onToggle={() => toggleSection("materialInfo")}
          >
            {/* 자재 검색 */}
            <div>
              <label className="text-gray-500 text-xs mb-1 block">
                자재 검색
              </label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Search
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    value={itemSearch}
                    onChange={(e) => setItemSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && fetchItems()}
                    placeholder="자재 코드/명 검색"
                    className="w-full pl-9 pr-2 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-all"
                  />
                </div>
                <button
                  type="button"
                  onClick={fetchItems}
                  className="px-4 py-2 rounded-xl bg-gray-900 text-white text-sm font-medium hover:bg-gray-800"
                >
                  {itemLoading ? "..." : "검색"}
                </button>
              </div>
            </div>

            {/* 검색 결과 리스트 */}
            {showItemList && itemResults.length > 0 && (
              <div className="mt-2 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden max-h-48 overflow-y-auto">
                {itemResults.map((it) => (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => selectItem(it)}
                    className="w-full text-left px-4 py-3 border-b border-gray-50 last:border-b-0 hover:bg-blue-50 active:bg-blue-100 transition-colors"
                  >
                    <div className="font-medium text-sm text-gray-800">
                      {it.name}{" "}
                      <span className="text-gray-400 text-xs">({it.code})</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {it.spec || "규격없음"} · {it.unit || "EA"}
                    </div>
                  </button>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 mt-2">
              <InputField
                label="자재명"
                value={officialMeta.material_name}
                onChange={(v) => onChangeMeta("material_name", v)}
              />
              <InputField
                label="규격"
                value={officialMeta.spec}
                onChange={(v) => onChangeMeta("spec", v)}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <InputField
                label="수량"
                type="number"
                value={officialMeta.quantity}
                onChange={(v) => onChangeMeta("quantity", v)}
              />
              <InputField
                label="단위"
                value={officialMeta.unit}
                onChange={(v) => onChangeMeta("unit", v)}
              />
            </div>
          </Section>

          {/* 4. 검사 항목 */}
          <Section
            title="검사 항목"
            expanded={expandedSections.inspection}
            onToggle={() => toggleSection("inspection")}
            badge={`${counts.total} 항목`}
          >
            <div className="space-y-4">
              {inspectionItems.map((row, idx) => (
                <div
                  key={idx}
                  className="rounded-xl border border-gray-200 bg-gray-50/50 p-4 space-y-3"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-gray-500">
                      NO. {idx + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeRow(idx)}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <input
                    type="text"
                    value={row.item}
                    onChange={(e) => updateRow(idx, "item", e.target.value)}
                    placeholder="검사 항목 (예: 외관 상태)"
                    className="w-full bg-transparent border-b border-gray-200 py-1 text-sm font-medium placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                  />
                  <input
                    type="text"
                    value={row.criteria}
                    onChange={(e) => updateRow(idx, "criteria", e.target.value)}
                    placeholder="판정 기준 (예: 긁힘 없을 것)"
                    className="w-full bg-transparent border-b border-gray-200 py-1 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                  />

                  {/* 판정 버튼 그룹 */}
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => updateRow(idx, "result", "pass")}
                      className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-bold transition-all ${
                        row.result === "pass"
                          ? "bg-green-500 text-white shadow-md shadow-green-200"
                          : "bg-white border border-gray-200 text-gray-400"
                      }`}
                    >
                      <Check size={16} /> 합격
                    </button>
                    <button
                      type="button"
                      onClick={() => updateRow(idx, "result", "fail")}
                      className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-bold transition-all ${
                        row.result === "fail"
                          ? "bg-red-500 text-white shadow-md shadow-red-200"
                          : "bg-white border border-gray-200 text-gray-400"
                      }`}
                    >
                      <X size={16} /> 불합격
                    </button>
                  </div>

                  <input
                    type="text"
                    value={row.remark}
                    onChange={(e) => updateRow(idx, "remark", e.target.value)}
                    placeholder="비고 / 특이사항"
                    className="w-full bg-white px-3 py-2 rounded-lg border border-gray-200 text-xs focus:border-gray-400 focus:outline-none"
                  />
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={addRow}
              className="w-full mt-2 flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed border-blue-300 bg-blue-50/50 text-blue-600 font-medium text-sm hover:bg-blue-50 transition-colors"
            >
              <Plus size={16} />
              항목 추가하기
            </button>
          </Section>

          {/* 5. 비고 */}
          <Section
            title="종합 의견"
            expanded={expandedSections.note}
            onToggle={() => toggleSection("note")}
          >
            <textarea
              value={form.note}
              onChange={(e) => onChange("note", e.target.value)}
              placeholder="종합 의견을 입력하세요."
              rows={3}
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm focus:border-blue-500 focus:outline-none resize-none"
            />
          </Section>

          {/* 6. 검사자 정보 */}
          <Section
            title="서명 정보"
            expanded={expandedSections.inspector}
            onToggle={() => toggleSection("inspector")}
          >
            <div className="grid grid-cols-3 gap-2">
              <InputField
                label="검사자"
                value={officialMeta.inspector}
                onChange={(v) => onChangeMeta("inspector", v)}
              />
              <InputField
                label="확인자"
                value={officialMeta.reviewer}
                onChange={(v) => onChangeMeta("reviewer", v)}
              />
              <InputField
                label="승인자"
                value={officialMeta.approver}
                onChange={(v) => onChangeMeta("approver", v)}
              />
            </div>
          </Section>

          {/* 7. 첨부자료 (모바일 전용) */}
          <Section
            title="사진 및 첨부"
            expanded={expandedSections.attachment}
            onToggle={() => toggleSection("attachment")}
            icon={<Paperclip size={16} className="text-gray-500" />}
          >
            <MobileAttachmentBox ref={attachmentRef} />
          </Section>
        </div>
      </main>

      {/* 하단 고정 액션바 (Safe Area 대응) */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 pb-safe z-50 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
        <div className="flex gap-3 max-w-screen-md mx-auto">
          <button
            type="button"
            className="flex-1 h-12 rounded-xl border border-gray-200 bg-gray-50 text-gray-700 font-bold text-sm hover:bg-gray-100 transition-colors"
            onClick={handleBack}
          >
            취소
          </button>
          <button
            type="button"
            className="flex-[2] h-12 rounded-xl bg-blue-600 text-white font-bold text-sm shadow-lg shadow-blue-200 hover:bg-blue-700 disabled:opacity-50 transition-all"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "저장 중..." : "저장하기"}
          </button>
        </div>
      </footer>
    </div>
  );
}

// ------------------------------------------------
// Sub Components (가독성을 위해 하단 분리)
// ------------------------------------------------

function SummaryBox({ label, value, color }) {
  return (
    <div className="flex flex-col items-center justify-center py-2 rounded-xl bg-gray-50 border border-gray-100">
      <div className={`text-xl font-bold ${color}`}>{value}</div>
      <div className="text-[11px] text-gray-500 mt-1">{label}</div>
    </div>
  );
}

function Section({ title, expanded, onToggle, badge, icon, children }) {
  return (
    <section className="rounded-2xl border border-gray-200 bg-white overflow-hidden transition-all duration-200">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3.5 bg-white active:bg-gray-50"
      >
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-bold text-sm text-gray-800">{title}</h3>
          {badge && (
            <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 text-[10px] font-bold">
              {badge}
            </span>
          )}
        </div>
        <ChevronDown
          size={18}
          className={`text-gray-400 transition-transform duration-200 ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>
      {expanded && (
        <div className="px-4 pb-5 pt-1 space-y-3 border-t border-gray-100 animate-fadeIn">
          {children}
        </div>
      )}
    </section>
  );
}

function InputField({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  disabled,
}) {
  return (
    <div>
      <label className="text-gray-500 text-xs font-medium mb-1.5 block ml-1">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm placeholder-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-all disabled:bg-gray-50"
      />
    </div>
  );
}
